import type { ReactNode } from "react";
import { createContext, useContext } from "react";
import { ChatModals } from "@/components/common/ChatModals";
import { useSendMessageToUser } from "@/hooks/useSendMessageToUser";

export type ChatModalContextValue = ReturnType<typeof useSendMessageToUser>;

const ChatModalContext = createContext<ChatModalContextValue | undefined>(
  undefined,
);

interface ChatModalProviderProps {
  children: ReactNode;
}

export const ChatModalProvider = ({ children }: ChatModalProviderProps) => {
  const chatModalState = useSendMessageToUser();

  return (
    <ChatModalContext.Provider value={chatModalState}>
      {children}
      <ChatModals
        isSearchModalOpen={chatModalState.isSearchModalOpen}
        isCreateChatModalOpen={chatModalState.isCreateChatModalOpen}
        selectedUserForChat={chatModalState.selectedUserForChat}
        createModalUser={chatModalState.createModalUser}
        searchModalInitialMode={chatModalState.searchModalInitialMode}
        searchModalInitialParticipants={
          chatModalState.searchModalInitialParticipants
        }
        onChatRoomSelect={chatModalState.handleChatRoomSelect}
        onCreateNewChat={chatModalState.handleCreateNewChat}
        onCloseSearchModal={chatModalState.handleCloseSearchModal}
        onCloseCreateModal={chatModalState.handleCloseCreateModal}
      />
    </ChatModalContext.Provider>
  );
};

export const useChatModals = () => {
  const context = useContext(ChatModalContext);
  if (!context) {
    throw new Error("useChatModals must be used within a ChatModalProvider");
  }
  return context;
};

export const useOptionalChatModals = () => {
  return useContext(ChatModalContext);
};
